/**
 * A handler or a writer that fails validation stores the validator's issue
 * array verbatim, so the error arrives as a wall of JSON that reads to whoever
 * wrote the schema and to nobody else. Pulling the issues apart lets a panel
 * say which field was rejected and why, and keep the original text one click
 * away.
 */
export interface FailureIssue {
  /** Dotted path to the rejected field, `commits[0].ticket`. Null when the issue is not field-scoped. */
  path: string | null;
  message: string;
  expected: string | null;
  received: string | null;
}

export type ParsedFailure =
  | { kind: "issues"; issues: FailureIssue[] }
  | { kind: "json"; text: string }
  | { kind: "text"; text: string };

/**
 * Classifies whatever the backend wrote. Anything that is not a recognisable
 * issue array falls back to JSON or to the raw sentence, since a half-read
 * error is worse than the original text.
 */
export function parseFailureMessage(message: string | null): ParsedFailure {
  const text = message?.trim() ?? "";
  if (text.length === 0) return { kind: "text", text };

  const parsed = parseJsonLoosely(text);
  if (parsed === undefined) return { kind: "text", text };

  const issues = toIssues(parsed);
  if (issues) return { kind: "issues", issues };

  return { kind: "json", text: JSON.stringify(parsed, null, 2) };
}

/**
 * Services prefix the payload as often as not ("Validation failed: [...]"), so
 * a failed parse of the whole string is retried against the bracketed part.
 */
function parseJsonLoosely(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    // Fall through to the bracketed slice.
  }

  const start = text.search(/[[{]/);
  const end = Math.max(text.lastIndexOf("]"), text.lastIndexOf("}"));
  if (start === -1 || end <= start) return undefined;

  try {
    return JSON.parse(text.slice(start, end + 1));
  } catch {
    return undefined;
  }
}

/** Null unless every entry is an issue — a partial read would hide the rest. */
function toIssues(parsed: unknown): FailureIssue[] | null {
  const entries = toIssueArray(parsed);
  if (!entries || entries.length === 0) return null;

  const issues: FailureIssue[] = [];
  for (const entry of entries) {
    const issue = toIssue(entry);
    if (!issue) return null;
    issues.push(issue);
  }

  return issues;
}

function toIssueArray(parsed: unknown): unknown[] | null {
  if (Array.isArray(parsed)) return parsed;
  if (!isRecord(parsed)) return null;
  if (Array.isArray(parsed.issues)) return parsed.issues;
  if (Array.isArray(parsed.errors)) return parsed.errors;
  return null;
}

function toIssue(entry: unknown): FailureIssue | null {
  if (!isRecord(entry) || typeof entry.message !== "string" || entry.message.length === 0) {
    return null;
  }

  return {
    path: formatIssuePath(entry.path),
    message: entry.message,
    expected: typeof entry.expected === "string" ? entry.expected : null,
    received: typeof entry.received === "string" ? entry.received : null,
  };
}

function formatIssuePath(path: unknown): string | null {
  if (typeof path === "string") return path.length > 0 ? path : null;
  if (!Array.isArray(path)) return null;

  const formatted = path.reduce<string>((acc, segment) => {
    if (typeof segment === "number") return `${acc}[${segment}]`;
    if (typeof segment !== "string" || segment.length === 0) return acc;
    return acc.length > 0 ? `${acc}.${segment}` : segment;
  }, "");

  return formatted.length > 0 ? formatted : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
